import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signInWithCustomToken,
  signOut, 
  sendPasswordResetEmail,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  isSignInWithEmailLink,
  updateProfile,
  onAuthStateChanged,
  signInWithPopup
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { toast } from 'react-toastify';

const AuthContext = createContext();

// Get API base URL from environment
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

// Email link authentication configuration
const actionCodeSettings = {
  url: process.env.REACT_APP_EMAIL_LINK_CONTINUE_URL || 'http://localhost:3000/auth/verify-email',
  handleCodeInApp: true,
  dynamicLinkDomain:
    process.env.REACT_APP_FIREBASE_DYNAMIC_LINK_DOMAIN ||
    process.env.REACT_APP_FIREBASE_AUTH_DOMAIN ||
    'innbotics-a4170-d0300.firebaseapp.com'
};

export function useAuth() {
  return useContext(AuthContext);
}

const parseJSONSafe = async (response) => {
  const text = await response.text();

  if (!text) {
    return { data: null, raw: '', isJSON: false };
  }

  try {
    return { data: JSON.parse(text), raw: text, isJSON: true };
  } catch (error) {
    return { data: null, raw: text, isJSON: false };
  }
};

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [hotelStatus, setHotelStatus] = useState(null);
  const [hotelStatusLoading, setHotelStatusLoading] = useState(true);
  const [loading, setLoading] = useState(true);

  // Sign up with email and password
  const signup = async (emailOrUserData, password, additionalData) => {
    try {
      // Support both calling formats:
      // 1. signup(userData) - single object
      // 2. signup(email, password, additionalData) - separate params (legacy)
      let userData;
      if (typeof emailOrUserData === 'string') {
        // Legacy format: signup(email, password, additionalData)
        userData = {
          email: emailOrUserData,
          password: password,
          ...additionalData
        };
      } else {
        // New format: signup(userData)
        userData = emailOrUserData;
      }

      const { email, password: userPassword, fullName, phone, role, organization, dateOfBirth } = userData;
      
      // Trim and validate full_name
      const trimmedFullName = (fullName || userData.name || '').trim();
      if (!trimmedFullName) {
        throw new Error('Full name is required');
      }
      if (trimmedFullName.length < 2 || trimmedFullName.length > 100) {
        throw new Error('Full name must be between 2 and 100 characters');
      }
      
      const requestBody = {
        email,
        password: userPassword,
        full_name: trimmedFullName,
        phone: phone || null,
        date_of_birth: dateOfBirth || null,
        role: role || 'guest',
        organization: organization || null
      };
      
      console.log('Registration request:', requestBody);
      
      // Register user via backend (uses Firebase Admin SDK - bypasses client restrictions)
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody)
      });

      const { data: parsed, raw } = await parseJSONSafe(response);

      if (!response.ok) {
        // Show validation errors if available
        if (parsed?.errors && Array.isArray(parsed.errors)) {
          const errorMessages = parsed.errors.map(err => err.msg || err.message).join('. ');
          throw new Error(errorMessages);
        }
        throw new Error(parsed?.message || raw || 'Failed to create account');
      }

      const newUser = parsed?.data?.user;
      const customToken = parsed?.data?.customToken;
      setUserProfile(newUser || null);

      // Show success message with email verification prompt
      toast.success('🎉 Account created! Please check your email to verify your account.', {
        autoClose: 5000
      });

      // Auto sign-in. Prefer the one-time custom token returned by the backend
      // because email/password sign-in immediately after Admin SDK createUser
      // can fail with auth/invalid-credential when the Firebase project has
      // email-enumeration protection / Identity Platform features enabled.
      // Fall back to email/password if the backend didn't mint a custom token.
      try {
        let userCredential;
        if (customToken) {
          userCredential = await signInWithCustomToken(auth, customToken);
        } else {
          userCredential = await signInWithEmailAndPassword(auth, email, userPassword);
        }

        // Use Firebase's UID to set the session flag (not backend's firebaseUid field)
        // This ensures exact match with what onAuthStateChanged receives
        const sessionKey = `profile_fetched_${userCredential.user.uid}`;
        sessionStorage.setItem(sessionKey, 'true');
        console.log('💾 Marked profile as fetched using Firebase UID (signup):', userCredential.user.uid);

        return userCredential.user;
      } catch (signInError) {
        console.error('Auto sign-in failed:', signInError);
        toast.info('Account created! Please log in with your credentials.');
        throw signInError;
      }
    } catch (error) {
      console.error('Signup error:', error);
      
      // Handle specific errors with user-friendly messages
      let errorMessage = error.message || 'Failed to create account';
      
      if (error.message?.includes('already exists')) {
        errorMessage = 'An account with this email already exists.';
      } else if (error.message?.includes('weak password')) {
        errorMessage = 'Password is too weak. Please use at least 6 characters with uppercase, lowercase, and numbers.';
      } else if (error.message?.includes('invalid email')) {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.message?.includes('Required field missing')) {
        errorMessage = 'Please fill in all required fields.';
      } else if (error.message?.includes('exceed maximum length')) {
        errorMessage = 'One or more fields are too long.';
      } else if (error.message?.includes('Database schema error')) {
        errorMessage = 'System configuration error. Please contact support.';
      } else if (error.message?.includes('Failed to create user in database')) {
        errorMessage = 'Unable to create account. Please try again or contact support.';
      }
      
      toast.error(errorMessage);
      throw error;
    }
  };

  // Sign in with email and password
  const signin = async (email, password) => {
    try {
      // First, authenticate with backend to get custom token
      console.log('🔄 Calling API:', `${API_BASE_URL}/auth/login`);
      
      const response = await fetch(`${API_BASE_URL}/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password
        })
      });

      console.log('📡 Response status:', response.status);
      console.log('📡 Response ok:', response.ok);

      const { data: parsed, raw } = await parseJSONSafe(response);
      console.log('📊 Parsed data:', parsed);
      console.log('📊 Raw response:', raw);

      if (!response.ok) {
        throw new Error(parsed?.message || raw || 'Failed to sign in');
      }

      if (!parsed?.data?.user) {
        throw new Error('Unexpected response from server. Please try again later.');
      }

      // Set profile in state and mark as fetched BEFORE signing into Firebase
      // This prevents onAuthStateChanged from trying to fetch the profile again
      const user = parsed.data.user;
      setUserProfile(user);

      // Sign in to Firebase with custom token
      if (parsed.customToken) {
        console.log('🔑 Signing in with custom token');
        const userCredential = await signInWithCustomToken(auth, parsed.customToken);
        console.log('✅ Firebase sign-in successful:', userCredential.user.uid);
        
        // Use Firebase's UID (from the credential) to set the session flag
        // This ensures the UID matches exactly what onAuthStateChanged will receive
        const sessionKey = `profile_fetched_${userCredential.user.uid}`;
        sessionStorage.setItem(sessionKey, 'true');
        console.log('💾 Marked profile as fetched using Firebase UID:', userCredential.user.uid);
        
        // For owners, fetch hotel status immediately after signin
        if (user.role === 'owner') {
          console.log('👤 Owner signin - fetching hotel status...');
          setHotelStatusLoading(true);
          try {
            const idToken = await userCredential.user.getIdToken(false);
            const hotelStatusResponse = await fetch(`${API_BASE_URL}/hotels/my-status`, {
              headers: {
                'Authorization': `Bearer ${idToken}`
              }
            });
            
            if (hotelStatusResponse.ok) {
              const hotelStatusResult = await hotelStatusResponse.json();
              console.log('🏨 Hotel status fetched:', hotelStatusResult.data);
              setHotelStatus(hotelStatusResult.data);
            } else if (hotelStatusResponse.status === 404) {
              console.log('📋 No hotel registration found');
              setHotelStatus({ status: 'not_registered' });
            } else {
              console.warn('⚠️ Hotel status fetch failed:', hotelStatusResponse.status);
              setHotelStatus({ status: 'not_registered' });
            }
          } catch (hotelError) {
            console.error('❌ Failed to fetch hotel status:', hotelError);
            setHotelStatus({ status: 'not_registered' });
          } finally {
            setHotelStatusLoading(false);
          }
        }
      } else {
        console.error('❌ No custom token received from backend');
        throw new Error('Authentication failed: No custom token received');
      }

      toast.success('Signed in successfully!');
      
      // Return user object with role for redirect handling
      return { user, role: user.role };
    } catch (error) {
      console.error('Sign in error:', error);
      
      // Handle specific Firebase auth errors
      let errorMessage = error.message;
      if (error.code === 'auth/user-not-found') {
        errorMessage = 'No account found with this email address.';
      } else if (error.code === 'auth/wrong-password') {
        errorMessage = 'Incorrect password. Please try again.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.code === 'auth/user-disabled') {
        errorMessage = 'This account has been disabled.';
      } else if (error.code === 'auth/admin-restricted-operation') {
        errorMessage = 'Authentication is currently restricted. Please contact administrator.';
      }
      
      toast.error(errorMessage);
      throw error;
    }
  };

  // Google Sign-In function
  const signInWithGoogle = async () => {
    try {
      console.log('🚀 Starting Google Sign-In...');
      console.log('Firebase Auth:', !!auth);
      console.log('Google Provider:', !!googleProvider);
      
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      console.log('Google Sign-In successful:', user.email);
      
      const markGoogleProfileFetched = () => {
        const sessionKey = `profile_fetched_${user.uid}`;
        sessionStorage.setItem(sessionKey, 'true');
      };

      const loginBackendWithFirebaseUid = async () => {
        const loginResponse = await fetch(`${API_BASE_URL}/auth/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            firebaseUid: user.uid,
          }),
        });
        const { data: loginResult, raw: loginRaw } = await parseJSONSafe(loginResponse);
        if (!loginResponse.ok) {
          throw new Error(loginResult?.message || loginRaw || 'Failed to validate user');
        }
        setUserProfile(loginResult?.data?.user || null);
        markGoogleProfileFetched();
        toast.success('Signed in with Google successfully!');
        return user;
      };

      // Register new Postgres row, or link / re-link UID; then fallback to login if already registered
      try {
        const response = await fetch(`${API_BASE_URL}/auth/register/google`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            email: user.email,
            password: 'google-oauth-' + user.uid,
            fullName: user.displayName || user.email.split('@')[0],
            firebaseUid: user.uid,
          }),
        });

        const { data: registerResult, raw: registerRaw } = await parseJSONSafe(response);

        if (response.ok) {
          const msg =
            registerResult?.message === 'Google account linked successfully'
              ? 'Account linked with Google!'
              : 'Account created successfully with Google!';
          setUserProfile(registerResult?.data?.user || null);
          markGoogleProfileFetched();
          toast.success(msg);
          return user;
        }

        const alreadyRegistered =
          response.status === 400 && registerResult?.message?.includes('already exists');
        const staleUidConflict = response.status === 409;

        if (alreadyRegistered || staleUidConflict) {
          return loginBackendWithFirebaseUid();
        }

        throw new Error(registerResult?.message || registerRaw || 'Failed to register user');
      } catch (backendError) {
        console.error('Backend error:', backendError);
        await signOut(auth);
        throw backendError;
      }
    } catch (error) {
      console.error('Google Sign-In error:', error);
      let errorMessage = 'Failed to sign in with Google';
      
      if (error.code) {
        switch (error.code) {
          case 'auth/popup-closed-by-user':
            errorMessage = 'Sign-in popup was closed';
            break;
          case 'auth/popup-blocked':
            errorMessage = 'Sign-in popup was blocked by browser';
            break;
          case 'auth/cancelled-popup-request':
            errorMessage = 'Sign-in was cancelled';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Network error. Please check your connection';
            break;
          case 'auth/unauthorized-domain':
            errorMessage = 'This domain is not authorized for Google Sign-In';
            break;
          case 'auth/operation-not-allowed':
            errorMessage = 'Google Sign-In is not enabled';
            break;
          default:
            errorMessage = error.message || 'Unknown Firebase error';
        }
      } else if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Google Sign-In is not enabled in Firebase Console. Please enable Google authentication.';
      } else {
        errorMessage = error.message || 'Unknown error occurred';
      }
      
      toast.error(errorMessage);
      throw error;
    }
  };

  // Sign out function
  const logout = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
      setHotelStatus(null);
      setHotelStatusLoading(false);
      setFetchedUserId(null);
      // Clear all session storage flags
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('profile_fetched_') || key.startsWith('last_status_refresh_')) {
          sessionStorage.removeItem(key);
        }
      });
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('innbotics_chat_')) {
          localStorage.removeItem(key);
        }
      });
      toast.success('Signed out successfully!');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to sign out');
      throw error;
    }
  };

  // Password reset
  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      toast.success('Password reset email sent!');
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(error.message);
      throw error;
    }
  };

  // Email link sign-in (passwordless)
  const sendEmailLink = async (email) => {
    try {
      await sendSignInLinkToEmail(auth, email, actionCodeSettings);
      // Save email locally for verification
      window.localStorage.setItem('emailForSignIn', email);
      toast.success('Sign-in link sent to your email!');
      return true;
    } catch (error) {
      console.error('Email link error:', error);
      let errorMessage = error.message;
      if (error.code === 'auth/operation-not-allowed') {
        errorMessage = 'Email link sign-in is not enabled in Firebase Console. Please enable "Email link (passwordless sign-in)" in Authentication > Sign-in method.';
      } else if (error.code === 'auth/admin-restricted-operation') {
        errorMessage = 'Email link sign-in is restricted. Please use alternative sign-in method.';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (error.code === 'auth/unauthorized-domain') {
        errorMessage = 'This domain is not authorized. Please add localhost to Firebase authorized domains.';
      }
      toast.error(errorMessage);
      throw error;
    }
  };

  // Complete email link sign-in
  const completeEmailLinkSignIn = async (emailLink) => {
    try {
      if (isSignInWithEmailLink(auth, emailLink)) {
        let email = window.localStorage.getItem('emailForSignIn');
        if (!email) {
          email = window.prompt('Please provide your email for confirmation');
        }
        
        const result = await signInWithEmailLink(auth, email, emailLink);
        window.localStorage.removeItem('emailForSignIn');
        
        // Register user in backend if this is first sign-in
        try {
          const response = await fetch(`${API_BASE_URL}/auth/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              firebaseUid: result.user.uid
            })
          });
          
          const { data: parsed } = await parseJSONSafe(response);
          
          if (!response.ok && response.status === 404) {
            // User doesn't exist in backend, create them
            const registerResponse = await fetch(`${API_BASE_URL}/auth/register`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                email: result.user.email,
                password: 'email-link-' + result.user.uid,
                fullName: result.user.displayName || result.user.email.split('@')[0],
                firebaseUid: result.user.uid
              })
            });
            
            const { data: registerParsed } = await parseJSONSafe(registerResponse);
            if (registerResponse.ok) {
              setUserProfile(registerParsed?.data?.user || null);
            }
          } else if (response.ok) {
            setUserProfile(parsed?.data?.user || null);
          }
        } catch (backendError) {
          console.warn('Backend registration failed, continuing with Firebase auth:', backendError);
        }
        
        toast.success('Successfully signed in with email link!');
        return result.user;
      }
    } catch (error) {
      console.error('Email link completion error:', error);
      toast.error(error.message);
      throw error;
    }
  };

  // Update user profile
  const updateUserProfile = async (updates) => {
    try {
      if (currentUser) {
        await updateProfile(currentUser, updates);
        toast.success('Profile updated successfully!');
      }
    } catch (error) {
      console.error('Profile update error:', error);
      toast.error('Failed to update profile');
      throw error;
    }
  };

  // Refresh hotel status - call this after hotel registration submission
  const refreshHotelStatus = async () => {
    if (!currentUser || userProfile?.role !== 'owner') {
      console.log('⚠️ Cannot refresh hotel status: no user or not owner');
      return null;
    }

    // Prevent concurrent calls
    if (hotelStatusLoading) {
      console.log('⏳ Hotel status already loading, skipping...');
      return hotelStatus;
    }

    // Debounce: prevent calls within 2 seconds of each other
    const now = Date.now();
    const lastCallKey = `last_status_refresh_${currentUser.uid}`;
    const lastCall = sessionStorage.getItem(lastCallKey);
    
    if (lastCall && (now - parseInt(lastCall)) < 2000) {
      console.log('⏱️ Debouncing: last call was too recent, skipping...');
      return hotelStatus;
    }
    
    sessionStorage.setItem(lastCallKey, now.toString());

    console.log('🔄 Refreshing hotel status...');
    setHotelStatusLoading(true);
    try {
      // Don't force refresh token to avoid quota issues - use cached token
      const idToken = await currentUser.getIdToken(false);
      const hotelStatusResponse = await fetch(`${API_BASE_URL}/hotels/my-status`, {
        headers: {
          'Authorization': `Bearer ${idToken}`
        }
      });
      
      if (hotelStatusResponse.ok) {
        const hotelStatusResult = await hotelStatusResponse.json();
        const newStatus = hotelStatusResult.data;
        
        // Set the status
        setHotelStatus(newStatus);
        setHotelStatusLoading(false);
        
        // Wait a tick for React to process state update
        await new Promise(resolve => setTimeout(resolve, 100));
        
        console.log('✅ Hotel status updated:', newStatus);
        return newStatus;
      } else if (hotelStatusResponse.status === 404) {
        console.log('📋 No hotel registration found (404)');
        const notRegistered = { status: 'not_registered' };
        setHotelStatus(notRegistered);
        setHotelStatusLoading(false);
        return notRegistered;
      } else if (hotelStatusResponse.status === 429) {
        console.error('⚠️ Rate limited (429) - too many requests');
        setHotelStatusLoading(false);
        toast.warning('Too many requests. Please wait a moment.');
        return hotelStatus; // Return current status
      } else {
        // Handle other errors
        console.error('❌ Failed to fetch hotel status:', hotelStatusResponse.status);
        setHotelStatusLoading(false);
        return null;
      }
    } catch (hotelError) {
      console.error('❌ Failed to refresh hotel status:', hotelError);
      const notRegistered = { status: 'not_registered' };
      setHotelStatus(notRegistered);
      setHotelStatusLoading(false);
      return notRegistered;
    }
  };

  // Track if we've already fetched profile for this user to prevent loops
  const [fetchedUserId, setFetchedUserId] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔐 Auth state changed:', user?.uid);
      setCurrentUser(user);
      
      if (user) {
        // Check sessionStorage to prevent refetch during same session
        const sessionKey = `profile_fetched_${user.uid}`;
        const alreadyFetchedInSession = sessionStorage.getItem(sessionKey);
        
        // Only fetch profile if we haven't fetched it for this user in this session
        if (!alreadyFetchedInSession) {
          console.log('📥 Fetching user profile for:', user.uid);
          
          // Wait a brief moment to allow any in-flight login/signup to set the flag
          await new Promise(resolve => setTimeout(resolve, 100));
          
          // Check again after waiting
          const stillNotFetched = !sessionStorage.getItem(sessionKey);
          if (!stillNotFetched) {
            console.log('✅ Profile was fetched during wait, skipping fetch');
            setLoading(false);
            return;
          }
          
          setFetchedUserId(user.uid);
          sessionStorage.setItem(sessionKey, 'true');
          
          try {
            const response = await fetch(`${API_BASE_URL}/auth/profile/${user.uid}`);
            
            // Handle deleted user (404) or invalid token (401)
            if (response.status === 404 || response.status === 401) {
              console.warn('⚠️ User not found in database or invalid token. Logging out...');
              toast.warning('Your session is invalid. Please log in again.');
              await signOut(auth);
              setUserProfile(null);
              setHotelStatus(null);
              setHotelStatusLoading(false);
              setLoading(false);
              setFetchedUserId(null);
              sessionStorage.removeItem(sessionKey);
              return;
            }
            
            if (response.ok) {
              const result = await response.json();
              const fetchedProfile = result.data.user;
              console.log('✅ User profile fetched:', fetchedProfile?.email);
              setUserProfile(fetchedProfile);
              
              // If user is a hotel owner, fetch their hotel registration status
              if (fetchedProfile?.role === 'owner') {
                console.log('👤 User is owner, fetching hotel status...');
                setHotelStatusLoading(true);
                try {
                  const idToken = await user.getIdToken(false);
                  const hotelStatusResponse = await fetch(`${API_BASE_URL}/hotels/my-status`, {
                    headers: {
                      'Authorization': `Bearer ${idToken}`
                    }
                  });
                  
                  if (hotelStatusResponse.ok) {
                    const hotelStatusResult = await hotelStatusResponse.json();
                    console.log('🏨 Hotel status:', hotelStatusResult.data);
                    setHotelStatus(hotelStatusResult.data);
                  } else if (hotelStatusResponse.status === 404) {
                    console.log('📋 No hotel registration found');
                    setHotelStatus({ status: 'not_registered' });
                  } else {
                    console.warn('⚠️ Hotel status fetch failed:', hotelStatusResponse.status);
                    setHotelStatus({ status: 'not_registered' });
                  }
                } catch (hotelError) {
                  console.error('❌ Failed to fetch hotel status:', hotelError);
                  setHotelStatus({ status: 'not_registered' });
                } finally {
                  setHotelStatusLoading(false);
                }
              } else {
                // Not a hotel owner, no need to fetch hotel status
                setHotelStatusLoading(false);
              }
            } else {
              console.error('❌ Profile fetch failed:', response.status);
              setHotelStatusLoading(false);
            }
          } catch (error) {
            console.error('❌ Failed to fetch user profile:', error);
            // If network error, don't log out - just set loading to false
            setHotelStatusLoading(false);
          }
        } else if (alreadyFetchedInSession) {
          console.log('ℹ️ Profile already fetched in this session, skipping profile fetch');
          
          // If profile is not in state but session says it was fetched, refetch it
          if (!userProfile) {
            console.log('⚠️ Profile not in state, refetching...');
            try {
              const response = await fetch(`${API_BASE_URL}/auth/profile/${user.uid}`);
              if (response.ok) {
                const result = await response.json();
                const fetchedProfile = result.data.user;
                console.log('✅ User profile refetched:', fetchedProfile?.email);
                setUserProfile(fetchedProfile);
              }
            } catch (error) {
              console.error('❌ Failed to refetch user profile:', error);
            }
          }
          
          // Even though profile is fetched, we still need to fetch hotel status for owners
          // Check if user profile exists in state and is an owner
          if (userProfile?.role === 'owner') {
            console.log('👤 User is owner, fetching hotel status...');
            setHotelStatusLoading(true);
            try {
              const idToken = await user.getIdToken(false);
              const hotelStatusResponse = await fetch(`${API_BASE_URL}/hotels/my-status`, {
                headers: {
                  'Authorization': `Bearer ${idToken}`
                }
              });
              
              if (hotelStatusResponse.ok) {
                const hotelStatusResult = await hotelStatusResponse.json();
                console.log('🏨 Hotel status:', hotelStatusResult.data);
                setHotelStatus(hotelStatusResult.data);
              } else if (hotelStatusResponse.status === 404) {
                console.log('📋 No hotel registration found');
                setHotelStatus({ status: 'not_registered' });
              } else {
                console.warn('⚠️ Hotel status fetch failed:', hotelStatusResponse.status);
                setHotelStatus({ status: 'not_registered' });
              }
            } catch (hotelError) {
              console.error('❌ Failed to fetch hotel status:', hotelError);
              setHotelStatus({ status: 'not_registered' });
            } finally {
              setHotelStatusLoading(false);
            }
          } else {
            setHotelStatusLoading(false);
          }
          
          setLoading(false);
        }
      } else {
        // User logged out
        console.log('🚪 User logged out');
        setUserProfile(null);
        setHotelStatus(null);
        setHotelStatusLoading(false);
        setFetchedUserId(null);
        // Clear all session flags
        Object.keys(sessionStorage).forEach(key => {
          if (key.startsWith('profile_fetched_')) {
            sessionStorage.removeItem(key);
          }
        });
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('innbotics_chat_')) {
            localStorage.removeItem(key);
          }
        });
      }
      
      setLoading(false);
    });

    return unsubscribe;
    // Intentionally NO dependencies - onAuthStateChanged manages its own lifecycle
  }, []);

  const [token, setToken] = useState(undefined);

  useEffect(() => {
    async function fetchToken() {
      if (currentUser) {
        try {
          const t = await currentUser.getIdToken(false);
          setToken(t);
        } catch (error) {
          console.warn('Failed to refresh Firebase token; continuing without token.', error);
          setToken(undefined);
        }
      } else {
        setToken(undefined);
      }
    }
    fetchToken();
  }, [currentUser]);

  const value = {
    currentUser,
    userProfile,
    hotelStatus,
    hotelStatusLoading,
    refreshHotelStatus,
    signup,
    signin,
    signInWithGoogle,
    logout,
    resetPassword,
    sendEmailLink,
    completeEmailLinkSignIn,
    updateUserProfile,
    loading,
    token
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

// Add helper function for role-based redirect
const getDefaultRedirectPath = (role) => {
  switch (role) {
    case 'admin':
      return '/admin';
    case 'manager':
      return '/manager';
    case 'owner':
      return '/owner';
    case 'guest':
    default:
      return '/guest';
  }
};

// Export only the helper function here (AuthContext, AuthProvider, and useAuth are already exported above)
export { getDefaultRedirectPath };